/**
 * SearchBar Component
 * Reusable search input with clear functionality and debounce support
 */

import { Button } from '@shared/components/ui/button'
import { Input } from '@shared/components/ui/input'
import { useDebounce } from '@shared/hooks'
import { Search, X } from 'lucide-react'
import { useEffect, useState } from 'react'

// ============================================================================
// Types
// ============================================================================

interface SearchBarProps {
  value?: string
  onChange: (value: string) => void
  placeholder?: string
  debounceMs?: number
  className?: string
}

// ============================================================================
// Component
// ============================================================================

export function SearchBar({
  value: controlledValue,
  onChange,
  placeholder = 'Buscar...',
  debounceMs = 500,
  className,
}: SearchBarProps) {
  const [inputValue, setInputValue] = useState(controlledValue || '')
  const debouncedValue = useDebounce(inputValue, debounceMs)

  // Update parent component when debounced value changes
  useEffect(() => {
    onChange(debouncedValue)
  }, [debouncedValue, onChange])

  // Sync with controlled value changes
  useEffect(() => {
    if (controlledValue !== undefined && controlledValue !== inputValue) {
      setInputValue(controlledValue)
    }
  }, [controlledValue])

  const handleClear = () => {
    setInputValue('')
    onChange('')
  }

  return (
    <div className={`relative ${className}`}>

      {/* Search Icon */}
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />

      {/* Input */}
      <Input
        type="text"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        placeholder={placeholder}
        className="pl-10 pr-10"
      />

      {/* Clear Button */}
      {inputValue && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleClear}
          className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Limpiar búsqueda</span>
        </Button>
      )}
    </div>
  )
}
